import { EFirebaseErrorMessage } from "constants/firebaseError";
import { EPath } from "constants/path";
import { SecureStorageKeys } from "constants/secureStorage";
import { differenceInHours, parseISO } from "date-fns";
import {
  AuthError,
  browserLocalPersistence,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  getRedirectResult,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  setPersistence,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  User,
  UserCredential,
  confirmPasswordReset,
  applyActionCode,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import React, { createContext, useContext, useEffect, useState } from "react";
import { secureStorage } from "utils/secureStorage";

import { authenticationApi } from "../apis";
import { auth, googleProvider } from "../config/firebase.config";
import {
  CompleteOnboardingRequest,
  GoogleSignInRequestUserTypeEnum,
  GoogleSignInResponseDto,
  PracticeProfileDto,
} from "../libs/cellera-sdk";

// Constants
const GOOGLE_PROVIDER_ID = "google.com";

export interface UserClaims {
  role?: string;
  requiresOnboarding?: boolean;
  permissions?: string[];
  tenantId?: string;
  practiceId?: string;
  isPracticeOwner?: boolean;
  isAwaitingProtocolDeployment?: boolean;
}

// Custom return types for authentication methods
interface EmailVerificationRequired {
  requiresEmailVerification: true;
  message: string;
  email: string;
}

export interface ResetPasswordParams {
  oobCode: string;
  newPassword: string;
}

interface AuthContextType {
  user: User | null;
  userClaims: UserClaims | null;
  isAuthenticated: boolean;
  refreshUserClaims: () => Promise<void>;
  signInWithGoogle: (
    userType: GoogleSignInRequestUserTypeEnum,
    rememberMe?: boolean
  ) => Promise<GoogleSignInResponseDto | undefined>;
  signInWithEmail: (
    email: string,
    password: string,
    userType: GoogleSignInRequestUserTypeEnum,
    rememberMe?: boolean
  ) => Promise<GoogleSignInResponseDto | EmailVerificationRequired>;
  signUpWithEmail: (
    email: string,
    password: string,
    practiceProfile: PracticeProfileDto
  ) => Promise<EmailVerificationRequired>;
  verifyEmail: (oobCode: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: ({
    oobCode,
    newPassword,
  }: ResetPasswordParams) => Promise<void>;
  updateOwnPassword: (
    currentPassword: string,
    newPassword: string
  ) => Promise<void>;
  completeOnboarding: (
    onboardingData: CompleteOnboardingRequest
  ) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userClaims, setUserClaims] = useState<UserClaims | null>(null);
  const isAuthenticated = user !== null;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Check if email is verified for email/password users
        // Google users are automatically verified, so we allow them through
        const isGoogleUser = user.providerData.some(
          (provider) => provider.providerId === GOOGLE_PROVIDER_ID
        );

        if (!isGoogleUser && !user.emailVerified) {
          // Sign out unverified email/password users
          await signOut(auth);
          setUser(null);
          setUserClaims(null);
          // Note: SDK will automatically handle authorization headers
          setLoading(false);
          return;
        }

        // Get custom claims and fresh token
        const idTokenResult = await user.getIdTokenResult(true);
        setUserClaims(idTokenResult.claims as UserClaims);
        setUser(user);
        // Note: SDK will automatically handle authorization headers
      } else {
        setUser(null);
        setUserClaims(null);
        // Note: SDK will automatically handle authorization headers
      }
      setLoading(false);
    });

    // Handle redirect result for mobile or popup blockers
    getRedirectResult(auth)
      .then((result) => {
        if (result) {
          handleGoogleSignInSuccess(
            result,
            GoogleSignInRequestUserTypeEnum.Provider
          );
        }
      })
      .catch((error) => {
        console.error("Google sign-in redirect error:", error);
      });

    return unsubscribe;
  }, []);

  const signInWithGoogle = async (
    userType: GoogleSignInRequestUserTypeEnum,
    rememberMe: boolean = false
  ) => {
    try {
      setLoading(true);

      // Set persistence based on remember me option
      await setPersistence(
        auth,
        rememberMe ? browserLocalPersistence : browserSessionPersistence
      );

      // Try popup first, fallback to redirect
      let result;
      try {
        result = await signInWithPopup(auth, googleProvider);
      } catch (popupError: unknown) {
        const error = popupError as AuthError;
        if (error.code === "auth/popup-blocked") {
          // Fallback to redirect for mobile or popup blockers
          await signInWithRedirect(auth, googleProvider);
          return; // Will be handled by redirect result
        }
        throw error;
      }

      return await handleGoogleSignInSuccess(result, userType);
    } catch (error) {
      if (error instanceof Error) {
        const blockingError = extractFirebaseBlockingMessage(error.message);
        if (blockingError.message) {
          throw new Error(blockingError.message);
        }
      }
      setLoading(false);
      throw handleAuthError(error);
    }
  };

  const refreshUserClaims = async () => {
    if (!auth.currentUser) return;
    const idTokenResult = await auth.currentUser.getIdTokenResult(true);
    setUserClaims(idTokenResult.claims as UserClaims);
  };

  const signInWithEmail = async (
    email: string,
    password: string,
    userType: GoogleSignInRequestUserTypeEnum,
    rememberMe: boolean = false
  ) => {
    try {
      setLoading(true);

      // Set persistence based on remember me option
      await setPersistence(
        auth,
        rememberMe ? browserLocalPersistence : browserSessionPersistence
      );

      const result = await signInWithEmailAndPassword(auth, email, password);

      // Check if email is verified before proceeding
      if (!result.user.emailVerified) {
        // check ìf link verification is expired base on lastSignInTime
        const creationTime = result.user.metadata.creationTime;
        const isExpired = isVerificationLinkExpired(creationTime);
        if (isExpired) {
          await sendEmailVerification(result.user, {
            url: window.location.origin + EPath.LOGIN,
          });
        }

        // Sign out unverified user
        setLoading(false);
        await signOut(auth); // Sign out unverified user
        return {
          requiresEmailVerification: true as const,
          message:
            "Please verify your email before signing in. Check your inbox for the verification link.",
          email: email,
        };
      }

      return await handleEmailAuthSuccess(result, userType);
    } catch (error) {
      setLoading(false);
      throw handleAuthError(error);
    }
  };

  const signUpWithEmail = async (
    email: string,
    password: string,
    practiceProfile: PracticeProfileDto
  ) => {
    try {
      setLoading(true);

      let result: UserCredential;

      try {
        result = await createUserWithEmailAndPassword(auth, email, password);
      } catch (error) {
        const signUpError = error as AuthError;
        if (signUpError.code === "auth/email-already-in-use") {
          result = (await handleEmailAlreadyInUse(
            email,
            password
          )) as UserCredential;
        } else {
          throw error;
        }
      }

      // Call the API with signup data
      await handleSaveDataSignup(
        result,
        practiceProfile,
        GoogleSignInRequestUserTypeEnum.Provider
      );

      // Send email verification with custom URL for auto-completion
      // if email not verified
      if (!result.user.emailVerified)
        await sendEmailVerification(result.user, {
          url: window.location.origin + EPath.LOGIN,
        });

      // Sign out the user immediately to prevent auto-login before verification
      await signOut(auth);

      // Always return verification message after signup - user needs to verify email first
      setLoading(false);
      return {
        requiresEmailVerification: true as const,
        message:
          "Please check your email and verify your account before signing in.",
        email: email,
      };
    } catch (error) {
      setLoading(false);
      throw handleAuthError(error);
    }
  };

  const handleEmailAlreadyInUse = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      if (result.user.emailVerified) {
        const error = Object.assign(
          new Error(EFirebaseErrorMessage.EMAIL_ALREADY_EXISTS),
          {
            code: "auth/email-already-in-use",
          }
        ) as AuthError;
        throw error;
      }
      return result;
    } catch (error) {
      const err = error as AuthError;
      console.log("err.code: ", err.code);
      if (err.code === "auth/wrong-password") {
        const error = Object.assign(
          new Error(EFirebaseErrorMessage.SIGN_UP_AGAIN_WITH_WRONG_PASSWORD),
          { code: "auth/sign-up-again-with-wrong-password" }
        ) as AuthError;
        throw error;
      }
      // If it's already an AuthError with a custom code, preserve it
      if (error instanceof Error && "code" in error) {
        throw error;
      }

      throw new Error(
        `Sign-in processing failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  const handleSaveDataSignup = async (
    result: UserCredential,
    practiceProfile: PracticeProfileDto,
    userType: GoogleSignInRequestUserTypeEnum = GoogleSignInRequestUserTypeEnum.Provider
  ) => {
    try {
      setLoading(true);

      // Get ID token and call the API with signup data
      const { user } = result;
      const idToken = await user.getIdToken();

      const response = await authenticationApi.googleSignIn({
        googleSignInRequest: {
          idToken,
          userType: userType,
          platform: "web",
          isSignUp: true,
          practiceProfile: { ...practiceProfile, isPracticeOwner: true },
        },
      });

      setLoading(false);
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Sign up failed. Please try again.";
      if (errorMessage === "errors.ERA_0125") {
        throw new Error(EFirebaseErrorMessage.PRACTICE_EMAIL_ALREADY_EXISTS);
      }

      setLoading(false);
      throw handleAuthError(error);
    }
  };

  const verifyEmail = async (oobCode: string) => {
    try {
      await applyActionCode(auth, oobCode);
    } catch (error) {
      throw handleAuthError(error);
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw handleAuthError(error);
    }
  };

  const resetPassword = async ({
    oobCode,
    newPassword,
  }: ResetPasswordParams) => {
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
    } catch (error) {
      throw handleAuthError(error);
    }
  };

  const updateOwnPassword = async (
    currentPassword: string,
    newPassword: string
  ) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        throw new Error("Not authenticated");
      }

      const credential = EmailAuthProvider.credential(
        currentUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);

      // After password update, refresh ID token and persist so subsequent
      // authenticated backend calls (like sending the confirmation email)
      // use a valid, non-revoked token.
      const freshIdToken = await currentUser.getIdToken(true);
      secureStorage.set(SecureStorageKeys.CUSTOM_TOKEN, freshIdToken);
    } catch (error) {
      throw handleAuthError(error);
    }
  };

  const handleEmailAuthSuccess = async (
    result: UserCredential,
    userType = "provider"
  ) => {
    try {
      const { user } = result;
      const idToken = await user.getIdToken();

      // Send email auth token to backend for processing using the same Google sign-in endpoint
      // The backend can differentiate between Google and email auth based on the token claims
      const response = await authenticationApi.googleSignIn({
        googleSignInRequest: {
          idToken,
          userType: userType as GoogleSignInRequestUserTypeEnum,
          platform: "web",
        },
      });

      // set custom token to local storage
      if (response.customToken) {
        const userCred = await signInWithCustomToken(
          auth,
          response.customToken
        );
        const idToken = await userCred.user.getIdToken();
        secureStorage.set(SecureStorageKeys.CUSTOM_TOKEN, idToken);
      }

      if (response.requiresOnboarding) {
        return { requiresOnboarding: true, userData: response };
      }

      return { success: true, user: response.user };
    } catch (error: unknown) {
      throw new Error(
        `${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  };

  const handleGoogleSignInSuccess = async (
    result: UserCredential,
    userType: GoogleSignInRequestUserTypeEnum
  ) => {
    try {
      const { user } = result;

      if (!user.email) {
        throw new Error("Google sign-in processing failed");
      }

      const idToken = await user.getIdToken();

      // Send Google ID token to backend for processing using SDK
      const response = await authenticationApi.googleSignIn({
        googleSignInRequest: {
          idToken,
          userType: userType as GoogleSignInRequestUserTypeEnum,
          platform: "web",
        },
      });

      // set custom token to local storage
      if (response.customToken) {
        const userCred = await signInWithCustomToken(
          auth,
          response.customToken
        );
        const idToken = await userCred.user.getIdToken();
        secureStorage.set(SecureStorageKeys.CUSTOM_TOKEN, idToken);
      }

      if (response.requiresOnboarding) {
        return { requiresOnboarding: true, userData: response };
      }

      return { success: true, user: response.user };
    } catch (error: unknown) {
      // If it's already an AuthError with a custom code, preserve it
      if (error instanceof Error && "code" in error) {
        throw error;
      }
      throw new Error(
        `Google sign-in processing failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  const completeOnboarding = async (
    onboardingData: CompleteOnboardingRequest
  ) => {
    try {
      const response = await authenticationApi.completeOnboarding({
        completeOnboardingRequest: onboardingData,
      });

      // Refresh user token to get updated claims
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
      }

      return response;
    } catch (error: unknown) {
      throw new Error(
        `Onboarding completion failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      // remove custom token from local storage
      secureStorage.remove(SecureStorageKeys.CUSTOM_TOKEN);
      // Note: SDK will automatically handle authorization headers
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleAuthError = (error: unknown) => {
    const authError = error as AuthError;

    if (!authError.code) {
      return error;
    }

    switch (authError.code) {
      case "auth/popup-closed-by-user":
        return new Error(EFirebaseErrorMessage.SIGN_IN_CANCELLED);
      case "auth/popup-blocked":
        return new Error(EFirebaseErrorMessage.POPUP_BLOCKED);
      case "auth/account-exists-with-different-credential":
        return new Error(
          EFirebaseErrorMessage.ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL
        );
      case "auth/operation-not-allowed":
        return new Error(EFirebaseErrorMessage.OPERATION_NOT_ALLOWED);
      case "auth/user-not-found":
        return new Error(EFirebaseErrorMessage.USER_NOT_FOUND);
      case "auth/wrong-password":
        return new Error(EFirebaseErrorMessage.WRONG_PASSWORD);
      case "auth/invalid-email":
        return new Error(EFirebaseErrorMessage.INVALID_EMAIL);
      case "auth/weak-password":
        return new Error(EFirebaseErrorMessage.WEAK_PASSWORD);
      case "auth/email-already-in-use":
        return new Error(EFirebaseErrorMessage.EMAIL_ALREADY_EXISTS);
      case "auth/too-many-requests":
        return new Error(EFirebaseErrorMessage.TOO_MANY_REQUESTS);
      case "auth/google-account-not-found":
        return new Error(EFirebaseErrorMessage.GOOGLE_ACCOUNT_NOT_FOUND);
      case "auth/expired-action-code":
        return new Error(EFirebaseErrorMessage.EXPIRED_ACTION_CODE);
      case "auth/invalid-action-code":
        return new Error(EFirebaseErrorMessage.INVALID_ACTION_CODE);
      case "auth/user-disabled":
        return new Error(EFirebaseErrorMessage.USER_DISABLED);

      case "auth/sign-up-again-with-wrong-password":
        return new Error(
          EFirebaseErrorMessage.SIGN_UP_AGAIN_WITH_WRONG_PASSWORD
        );
      default:
        return new Error(EFirebaseErrorMessage.AUTHENTICATION_FAILED);
    }
  };

  // Helper function to extract the error message from the Firebase blocking message
  const extractFirebaseBlockingMessage = (err: string) => {
    // Try to catch JSON in the string "returned an error: { ... } (auth/...)"
    const re = /err:\s*(\{[\s\S]*?\})\s*\(auth\//i; // dotAll by [\s\S], non-greedy
    const m = err.match(re) || err.match(/\{[\s\S]*\}/); // fallback: catch the first JSON

    if (!m) return { message: null, status: null };

    try {
      const json = JSON.parse(m[1] || m[0]);
      return {
        message: json?.error?.message || null,
        status: json?.error?.status || null,
      };
    } catch {
      return { message: null, status: null };
    }
  };

  // Helper function to check if creation time is more than 1 hour old
  const isVerificationLinkExpired = (time: string | undefined): boolean => {
    if (!time) {
      return false;
    }
    const creationDate = parseISO(time);
    const now = new Date();
    const hoursDifference = differenceInHours(now, creationDate);
    return hoursDifference > 1;
  };

  const value = {
    user,
    userClaims,
    refreshUserClaims,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    verifyEmail,
    forgotPassword,
    resetPassword,
    updateOwnPassword,
    completeOnboarding,
    logout,
    loading,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
